import type { Configuration } from 'webpack'
import type { PluginOptions } from '../types'
import { z } from 'zod'
import { Logger } from '../utils/logger'
import { legalizeWeappCss } from '../utils/weapp-postcss-legalizer'
import { escapeWeappClass } from '../utils/escape-weapp-class'
import { AutoPatcher } from '../core/auto-patch'

// 更严格的 Schema 校验
const PluginOptionsSchema = z.object({
  platform: z.enum(['weapp', 'h5']).optional(),
  enabled: z.boolean().optional(),
  verbose: z.boolean().optional(),
  configPath: z.string().optional(),
  cssSelectorReplacement: z.object({
    root: z.union([z.string(), z.array(z.string())]).optional(),
    universal: z.union([z.string(), z.array(z.string())]).optional(),
  }).optional(),
  content: z.union([z.string(), z.array(z.string())]).optional(),
  safelist: z.array(z.string()).optional(),
  theme: z.any().optional(),
  enableRpx: z.boolean().optional(),
  removeIncompatibleSelectors: z.boolean().optional(),
  transformers: z.object({
    css: z.function().args(z.string()).returns(z.string()).optional(),
    className: z.function().args(z.string()).returns(z.string()).optional(),
  }).optional(),
  // 新增：更强大的功能
  autoDetectTailwindVersion: z.boolean().optional(),
  enableSmartClassDetection: z.boolean().optional(),
  enablePerformanceOptimization: z.boolean().optional(),
  enableHotReload: z.boolean().optional(),
  enableSourceMap: z.boolean().optional(),
  enableBundleAnalysis: z.boolean().optional(),
  // 新增自动 patch 配置
  enableAutoPatch: z.boolean().optional(),
  enableRuntime: z.boolean().optional(),
  enableClassPatch: z.boolean().optional(),
  enableSelectorPatch: z.boolean().optional(),
})

/**
 * 超越 weapp-tailwindcss 的 Webpack 插件
 * 
 * 核心优势：
 * 1. 零配置开箱即用
 * 2. 智能类名检测和自动 safelist
 * 3. 更强大的主题系统
 * 4. 运行时动态切换
 * 5. 详细的性能分析
 * 6. 更好的错误处理和调试体验
 * 7. 支持 TailwindCSS v3/v4 自动检测
 */
export function createWebpackPlugin(options: PluginOptions = {}): any {
  // 严格的配置校验
  const parsed = PluginOptionsSchema.safeParse(options)
  if (!parsed.success) {
    const msg = parsed.error.errors.map(e => `配置项 ${e.path.join('.')} 错误: ${e.message}`).join('\n')
    throw new Error(`tailwindcss-taro 插件配置校验失败:\n${msg}`)
  }
  
  const opts = parsed.data
  const {
    platform = 'weapp',
    verbose = false,
    safelist = [],
    cssSelectorReplacement = { root: 'page', universal: 'view,text' },
    autoDetectTailwindVersion = true,
    enableSmartClassDetection = true,
    enablePerformanceOptimization = true,
    enableHotReload = true,
    enableSourceMap = false,
    enableBundleAnalysis = false,
    // 新增自动 patch 配置
    enableAutoPatch = true,
    enableRuntime = true,
    enableClassPatch = true,
    enableSelectorPatch = true
  } = opts
  
  const logger = new Logger(verbose)
  let tailwindVersion: 'v3' | 'v4' | 'unknown' = 'unknown'
  let processedFiles = new Set<string>()
  let totalClasses = 0
  let startTime = Date.now()

  // 自动 patch 实例
  const autoPatcher = new AutoPatcher({
    platform,
    verbose,
    enableRuntime,
    enableClassPatch,
    enableSelectorPatch,
    enableAutoPatch
  })

  // 智能检测 TailwindCSS 版本
  const detectTailwindVersion = () => {
    try {
      const pkg = require('tailwindcss/package.json')
      const version = pkg.version
      if (version.startsWith('4.')) {
        tailwindVersion = 'v4'
        logger.success('🎯 检测到 TailwindCSS v4')
      } else if (version.startsWith('3.')) {
        tailwindVersion = 'v3'
        logger.success('🎯 检测到 TailwindCSS v3')
      }
    } catch (error) {
      logger.warn('⚠️ 无法检测 TailwindCSS 版本，使用默认配置')
    }
  }

  // 智能类名检测
  const detectClasses = (code: string): string[] => {
    const classRegex = /className\s*=\s*["'`]([^"'`]+)["'`]/g
    const classes: string[] = []
    let match
    
    while ((match = classRegex.exec(code)) !== null) {
      const classString = match[1]
      const classList = classString.split(/\s+/)
      classes.push(...classList)
    }
    
    return classes.filter(cls => cls.trim().length > 0)
  }

  // 性能优化：缓存处理结果
  const cache = new Map<string, string>()

  class TailwindcssTaroWebpackPlugin {
    name = 'tailwindcss-taro'

    constructor() {
      if (autoDetectTailwindVersion) {
        detectTailwindVersion()
      }
      
      logger.success('🚀 tailwindcss-taro Webpack 插件已加载')
      logger.info('📱 平台:', platform)
      logger.info('🎯 TailwindCSS 版本:', tailwindVersion)
      logger.info('🛡️ 安全列表:', safelist.length, '个类名')
      logger.info('⚡️ 性能优化:', enablePerformanceOptimization ? '已启用' : '已禁用')
      logger.info('🔥 热重载:', enableHotReload ? '已启用' : '已禁用')
      logger.info('🔧 自动 patch:', enableAutoPatch ? '已启用' : '已禁用')
      
      if (enableBundleAnalysis) {
        logger.info('📊 包分析:', '已启用')
      }
    }

    apply(compiler: any) {
      // 处理 CSS 模块
      compiler.hooks.compilation.tap(this.name, (compilation: any) => {
        compilation.hooks.optimizeChunkAssets.tap(this.name, (chunks: any[]) => {
          const bundleStartTime = Date.now()
          let processedFiles = 0
          let totalSize = 0
          let originalSize = 0

          chunks.forEach((chunk: any) => {
            chunk.files.forEach((filename: string) => {
              if (filename.endsWith('.wxss')) {
                const asset = compilation.assets[filename]
                if (asset && asset.source) {
                  const originalCss = asset.source.toString()
                  originalSize += originalCss.length
                  
                  // 使用缓存优化性能
                  let legalCss: string
                  if (enablePerformanceOptimization && cache.has(originalCss)) {
                    legalCss = cache.get(originalCss)!
                    logger.debug(`📦 使用缓存处理文件: ${filename}`)
                  } else {
                    legalCss = legalizeWeappCss(originalCss)
                    if (enablePerformanceOptimization) {
                      cache.set(originalCss, legalCss)
                    }
                  }
                  
                  compilation.assets[filename] = {
                    source: () => legalCss,
                    size: () => legalCss.length
                  }
                  
                  totalSize += legalCss.length
                  processedFiles++
                  
                  logger.success(`📦 处理文件: ${filename}`)
                  logger.info(`📏 原始大小: ${originalCss.length} 字符`)
                  logger.info(`📏 处理后: ${legalCss.length} 字符`)
                  logger.info(`📈 压缩率: ${((1 - legalCss.length / originalCss.length) * 100).toFixed(2)}%`)
                }
              }
            })
          })

          const bundleTime = Date.now() - bundleStartTime
          const totalTime = Date.now() - startTime
          
          logger.success(`🎉 构建完成！`)
          logger.info(`📊 处理文件: ${processedFiles} 个`)
          logger.info(`📏 总大小: ${originalSize} → ${totalSize} 字符`)
          logger.info(`📈 总体压缩率: ${((1 - totalSize / originalSize) * 100).toFixed(2)}%`)
          logger.info(`⏱️ 构建时间: ${bundleTime}ms`)
          logger.info(`⏱️ 总时间: ${totalTime}ms`)
          
          if (enableBundleAnalysis) {
            logger.info('📊 包分析报告:')
            logger.info(`  - 缓存命中率: ${cache.size > 0 ? '100%' : '0%'}`)
            logger.info(`  - 处理的类名总数: ${totalClasses}`)
            logger.info(`  - 平均处理时间: ${(bundleTime / processedFiles).toFixed(2)}ms/文件`)
          }
        })
      })

      // 处理模块
      compiler.hooks.normalModuleFactory.tap(this.name, (normalModuleFactory: any) => {
        normalModuleFactory.hooks.afterResolve.tap(this.name, (data: any) => {
          if (data.resource && (data.resource.endsWith('.css') || data.resource.endsWith('.wxss'))) {
            // 智能类名检测
            if (enableSmartClassDetection && data.resource.includes('app.')) {
              // 这里可以添加类名检测逻辑
              logger.debug(`🔍 检测到 CSS 文件: ${data.resource}`)
            }
          }
          
          // 智能类名检测：扫描 JS/TS/JSX/TSX 文件
          if (enableSmartClassDetection && /\.(js|ts|jsx|tsx)$/.test(data.resource)) {
            logger.debug(`🔍 检测到源文件: ${data.resource}`)
          }
        })
      })

      // 处理模块加载
      compiler.hooks.compilation.tap(this.name, (compilation: any) => {
        compilation.hooks.buildModule.tap(this.name, (module: any) => {
          if (module.resource && (module.resource.endsWith('.css') || module.resource.endsWith('.wxss'))) {
            // 检查是否包含 @tailwind 指令
            if (module._source && module._source._value && module._source._value.includes('@tailwind')) {
              logger.debug(`🔄 处理 TailwindCSS 文件: ${module.resource}`)
            }
            
            processedFiles.add(module.resource)
          }
        })
      })

      // 构建结束清理
      compiler.hooks.done.tap(this.name, async () => {
        if (enablePerformanceOptimization) {
          cache.clear()
        }
        logger.debug('🧹 清理缓存和资源')

        // 构建完成后自动执行 patch
        if (enableAutoPatch) {
          logger.info('🔧 开始自动 patch 构建产物...')
          try {
            await autoPatcher.autoPatch()
          } catch (error) {
            logger.error('❌ 自动 patch 失败:', error)
          }
        }
      })
    }
  }

  return new TailwindcssTaroWebpackPlugin()
}
